import { useState, useEffect } from 'react';
import { get } from '../../../api/client';
import { formatLocalDate, toLocalDateString } from '../../../utils/dateUtils';
import type { OrderItem, PaginationMeta } from '../../../types';

export interface TrendPoint {
  label: string;
  key: string;
  orders: number;
  income: number;
}

export interface StatusCount {
  name: string;
  count: number;
  color: string;
  bg: string;
  icon: string;
  iconColor: string;
}

export interface HomeStatsData {
  pending: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  trend7: TrendPoint[];
  trend30: TrendPoint[];
  trend90: TrendPoint[];
  statusBreakdown: Array<{ name: string; value: number; color: string }>;
}

/** 不计入统计的订单状态 */
const EXCLUDED_STATUSES = ['已取消', '已拒绝'];

/** 判断订单是否应排除（已取消/已拒绝，注意"已拒绝"可能在 reviewStatus 字段中） */
function isOrderExcluded(o: { orderStatus?: string | null; reviewStatus?: string | null }): boolean {
  if (EXCLUDED_STATUSES.includes(o.orderStatus ?? '')) return true;
  if (o.reviewStatus === '已拒绝') return true;
  return false;
}

function buildDailyTrend(
  orders: Array<{ createdAt: string | null; orderAmount: number | null; orderCategory: string | null; orderStatus: string | null; reviewStatus?: string | null }>,
  days: number
): TrendPoint[] {
  const now = new Date();
  const map: Record<string, { orders: number; income: number }> = {};
  for (let i = 0; i < days; i++) {
    const d = new Date(now.getTime() - (days - 1 - i) * 86400000);
    // 与后端业务日趋势同一口径：按本地（东八区）日历日切桶，不是 UTC 日
    const key = formatLocalDate(d);
    map[key] = { orders: 0, income: 0 };
  }
  orders.forEach(o => {
    const day = toLocalDateString(o.createdAt);
    if (!day || !map[day]) return;
    // 已取消/已拒绝订单不计入趋势统计
    if (isOrderExcluded(o)) return;
    map[day].orders += 1;
    // 非支出订单统入收入
    if (!(o.orderCategory ?? '').includes('支出')) {
      map[day].income += Number(o.orderAmount) || 0;
    }
  });
  return Object.entries(map).map(([key, v]) => ({
    key,
    label: key.slice(5),
    orders: v.orders,
    income: v.income,
  }));
}

function buildMonthlyTrend(
  orders: Array<{ createdAt: string | null; orderStartDate: string | null; orderAmount: number | null; orderCategory: string | null; orderStatus: string | null; reviewStatus?: string | null }>,
  months: number
): TrendPoint[] {
  const now = new Date();
  const map: Record<string, { orders: number; income: number }> = {};
  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    map[key] = { orders: 0, income: 0 };
  }
  orders.forEach(o => {
    const key = (o.orderStartDate || o.createdAt || '').slice(0, 7);
    if (!map[key]) return;
    // 已取消/已拒绝订单不计入趋势统计
    if (isOrderExcluded(o)) return;
    map[key].orders += 1;
    // 非支出订单统入收入
    if (!(o.orderCategory ?? '').includes('支出')) {
      map[key].income += Number(o.orderAmount) || 0;
    }
  });
  return Object.entries(map).map(([key, v]) => {
    const month = parseInt(key.split('-')[1], 10);
    return { key, label: `${month}月`, orders: v.orders, income: v.income };
  });
}

const STATUS_COLORS: Record<string, string> = {
  '未执行': '#f59e0b',
  '执行中': '#06b6d4',
  '已完成': '#10b981',
  '已取消': '#f43f5e',
  '已归档': '#9ca3af',
};

export function useHomeStats() {
  const [data, setData] = useState<HomeStatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 获取所有订单
        const res = await get<{ data: OrderItem[]; pagination: PaginationMeta }>('/orders', { params: { pageSize: 9999 } });
        const allOrders = res?.data || [];
        const recent = allOrders;

        const incomeOrders = (allOrders || []).filter(o => !(o.orderCategory ?? '').includes('支出'));
        // 排除已拒绝/已取消的订单后再统计状态分布
        const activeIncomeOrders = incomeOrders.filter(o => !isOrderExcluded(o));
        const pending = activeIncomeOrders.filter(o => o.orderStatus === '未执行').length;
        const inProgress = activeIncomeOrders.filter(o => o.orderStatus === '执行中').length;
        const completed = activeIncomeOrders.filter(o => o.orderStatus === '已完成').length;
        const cancelled = incomeOrders.filter(o => o.orderStatus === '已取消').length;

        const statusMap: Record<string, number> = {};
        incomeOrders.forEach(o => {
          const s = o.orderStatus || '未知';
          statusMap[s] = (statusMap[s] || 0) + 1;
        });
        const statusBreakdown = Object.entries(statusMap)
          .map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] || '#9ca3af' }))
          .sort((a, b) => b.value - a.value);

        type RecentRow = {
          createdAt: string | null;
          orderStartDate: string | null;
          orderAmount: number | null;
          orderCategory: string | null;
          orderStatus: string | null;
        };
        const recentTyped = recent as RecentRow[];

        setData({
          pending,
          inProgress,
          completed,
          cancelled,
          trend7: buildDailyTrend(recentTyped, 7),
          trend30: buildDailyTrend(recentTyped, 30),
          trend90: buildMonthlyTrend(recentTyped, 3),
          statusBreakdown,
        });
      } catch (error) {
        console.error('获取首页统计数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
   
  }, []);

  return { data, loading };
}
